import { ReactNode } from 'react'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  StackProps,
  Stack,
  Box,
} from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'

type SectionAccordionProps = {
  title: string
  children: ReactNode
  spacing?: StackProps['spacing']
  titleAdornment?: ReactNode
}

export default function SectionAccordion({
  title,
  children,
  spacing = 0.5,
  titleAdornment,
}: SectionAccordionProps) {
  const [expanded, setExpanded] = useLocalStorage(
    `document-dialog-section-expanded-${title}`,
    true,
  )

  return (
    <Accordion
      expanded={expanded}
      elevation={0}
      onChange={(_, expanded) => setExpanded(expanded)}
      sx={{
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          mb: 0,
        },
      }}
    >
      <AccordionSummary
        // The summary is a button by default; an adornment button inside it
        // would be a button nested in a button
        component={titleAdornment ? 'div' : 'button'}
        expandIcon={<KeyboardArrowDown />}
        sx={{
          p: 0,
          mb: 0,
          minHeight: 'min-content !important',
          '& .MuiAccordionSummary-content': {
            margin: '0px !important',
          },
          '&.Mui-expanded': {
            mb: spacing,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary" variant="h6" component="h3">
            {title}
          </Typography>
          {expanded && titleAdornment && (
            // Keep clicks on the adornment from toggling the accordion
            <Box onClick={(event) => event.stopPropagation()}>
              {titleAdornment}
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>{children}</AccordionDetails>
    </Accordion>
  )
}
